/**
 * strategyExplainers.ts — the single source of the plain-English "what's this?" copy for the
 * strategy allocators (Manual Control across every module: Aged Care, Retirement Village,
 * Support at Home, downsizing).
 *
 * Why this file exists (owner, 2026-10-02): several strategy tooltips led straight into the
 * technical limit — "Up to $300,000 per person (55+)." — without first saying what the strategy
 * IS. The copy was also duplicated inline across five allocators, so it had drifted (three
 * different wordings of the gifting note alone). Each explainer now lives here ONCE: a plain-words
 * sentence on what the move is, then the limit/rule. Consumer-first — plain words, low jargon
 * (see the standing consumer-first presentation challenge), the limit kept as the second clause.
 *
 * Every string is a function so the limit constants are read lazily and the import stays safe to
 * use from any allocator without an eval-order cycle.
 */
import { formatCurrency } from '../utils/index.ts';
import {
    DOWNSIZER_CONTRIBUTION_CAP,
    GIFTING_LIMIT_ANNUAL,
    GIFTING_LIMIT_5_YEAR,
    FUNERAL_BOND_LIMIT,
} from '../constants/index.ts';

/** Shown on the downsizer fields when the client is under 55 — the one rule that blocks the move. */
export const DOWNSIZER_AGE_MESSAGE = 'You must be 55 or over to make a downsizer contribution.';

export const strategyExplainers = {
    /** Contribute sale proceeds to super. `isEligible` false → the age rule instead of the pitch. */
    downsizerToSuper: (isEligible: boolean): string =>
        isEligible
            ? `Move some of the money from selling your home into super, where earnings are taxed lightly — 15%, or nothing once it's paying you a pension. Up to ${formatCurrency(DOWNSIZER_CONTRIBUTION_CAP)} per person (55+).`
            : DOWNSIZER_AGE_MESSAGE,

    /** The same move on the partner's share of the sale. */
    partnerDownsizerToSuper: (isEligible: boolean): string =>
        isEligible
            ? `Your partner can do the same with their share of the sale — money into super, where earnings are taxed lightly. Up to ${formatCurrency(DOWNSIZER_CONTRIBUTION_CAP)} each (55+).`
            : DOWNSIZER_AGE_MESSAGE,

    /** Give money to family or friends. */
    giftToLovedOnes: (): string =>
        `Give some of the money to family or friends. Centrelink still counts anything above the limits as yours for five years, so large gifts may not lift your pension. Free area: ${formatCurrency(GIFTING_LIMIT_ANNUAL)} a year, or ${formatCurrency(GIFTING_LIMIT_5_YEAR)} over any five years.`,

    /** Prepay funeral costs through a funeral bond. */
    funeralBond: (): string =>
        `Set money aside now to prepay funeral costs. It's locked away for that purpose, and in return Centrelink leaves it out of the assets test — up to ${formatCurrency(FUNERAL_BOND_LIMIT)} per person — which can lift your pension.`,

    /** Turn a lump sum into guaranteed income. */
    buyAnnuity: (): string =>
        `Turn a lump sum into guaranteed income, for life or a set term. Centrelink counts only part of the value, which can lift your pension — useful when you want predictable income rather than relying only on market returns.`,

    /** Invest in shares / managed funds. */
    buyShares: (): string =>
        `Invest the money in shares or managed funds. It aims for higher long-term growth than cash, but returns aren't guaranteed. Centrelink counts it and deems an income from it.`,

    /** Buy an investment property. */
    buyInvestmentProperty: (): string =>
        `Buy an investment property with the money. The property counts as an asset and the net rent (after expenses) is counted as income. Expenses like rates, insurance and maintenance are generally tax-deductible — enter them below.`,

    /** Pay the retirement village entry fee from the sale. */
    villageEntryFee: (): string =>
        `Use sale proceeds to pay the retirement village entry fee (capped at the entry fee amount above).`,

    /** Pay down an existing loan with the proceeds. */
    payDownLoan: (): string =>
        `Use the money to pay down a loan you already have, cutting the interest you pay.`,
};

export type StrategyExplainerKey = keyof typeof strategyExplainers;
